import { talkPriceSubunitsFor } from '../../../util/talkPrices';
import React from 'react';
import { useForm } from 'react-final-form';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  SESSION_FORMAT_PRICES_SUBUNITS,
  SESSION_LENGTH_MINUTES,
  offeredFormatsFromPublicData,
  sessionFormatFromVariantName,
  variantNameForFormat,
} from '../../../util/sessionPricing';

import ValidationError from '../../ValidationError/ValidationError';

import css from './SessionFormatPicker.module.css';

const formatPrice = subunits => `$${Math.round(subunits / 100)}`;

/**
 * Booking-time picker for talksession format (chat / voice / video).
 * Sets both sessionFormat and the matching 15-minute price variant name.
 */
const SessionFormatPicker = props => {
  const intl = useIntl();
  const form = useForm();
  const { priceVariants = [], onPriceVariantChange, disabled, offeredFormats, highlight, listing = null } = props;
  const formats = offeredFormatsFromPublicData(
    offeredFormats ? { offeredFormats } : {}
  );

  const applyFormat = format => {
    const expectedName = variantNameForFormat(format);
    const variant =
      priceVariants.find(pv => sessionFormatFromVariantName(pv.name) === format) ||
      priceVariants.find(pv => pv.name === expectedName) ||
      priceVariants.find(pv => pv.bookingLengthInMinutes === SESSION_LENGTH_MINUTES) ||
      priceVariants[0];
    form.batch(() => {
      form.change('sessionFormat', format);
      form.change('priceVariantName', variant?.name || expectedName);
    });
    if (typeof onPriceVariantChange === 'function') {
      onPriceVariantChange(variant?.name || expectedName);
    }
  };

  return (
    <fieldset
      id="booking-session-format"
      data-session-format="options"
      data-session-format-highlight={highlight ? 'true' : 'false'}
      className={classNames(css.root, { [css.highlight]: highlight })}
      disabled={disabled}
    >
      <legend className={css.legend}>
        <FormattedMessage id="SessionFormatPicker.label" />
      </legend>
      <p className={css.hint}>
        <FormattedMessage
          id="SessionFormatPicker.hint"
          values={{ minutes: SESSION_LENGTH_MINUTES }}
        />
      </p>
      <Field
        name="sessionFormat"
        validate={value =>
          value ? undefined : intl.formatMessage({ id: 'SessionFormatPicker.required' })
        }
      >
        {({ input, meta }) => (
          <>
            <div
              className={css.options}
              role="radiogroup"
              aria-label={intl.formatMessage({ id: 'SessionFormatPicker.label' })}
            >
              {formats.map(format => {
                const selected = input.value === format;
                return (
                  <label
                    key={format}
                    className={classNames(css.option, { [css.optionSelected]: selected })}
                  >
                    <input
                      type="radio"
                      name="sessionFormat"
                      value={format}
                      checked={!!selected}
                      disabled={disabled}
                      onChange={() => {
                        try {
                          require('../../../util/bookingFunnel').trackBookingStep('format_pick', { format, mode: 'later' });
                        } catch (e) {
                          // ignore
                        }
                        input.onChange(format);
                        applyFormat(format);
                      }}
                    />
                    <span className={css.optionCopy}>
                      <span className={css.optionName}>
                        <FormattedMessage id={`SessionFormatPicker.${format}`} />
                      </span>
                      <span className={css.optionPrice}>
                        {(() => {
                          const sub = listing ? talkPriceSubunitsFor(listing, format) : null;
                          return sub === 0 ? 'Free' : formatPrice(sub ?? SESSION_FORMAT_PRICES_SUBUNITS[format]);
                        })()}
                      </span>
                      <span className={css.optionLength}>
                        <FormattedMessage
                          id="SessionFormatPicker.length"
                          values={{ minutes: SESSION_LENGTH_MINUTES }}
                        />
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
            <ValidationError fieldMeta={meta} />
          </>
        )}
      </Field>
      <Field name="priceVariantName" render={({ input }) => <input type="hidden" {...input} />} />
    </fieldset>
  );
};

export default SessionFormatPicker;
